// =============================================================================
// ui/tabs/Review.tsx — the weekly and monthly retrospective.
// -----------------------------------------------------------------------------
// A FREE-TIER tab (the upgrade card: "Best Opps and Review moved to Free"), and
// until this landed it rendered "Not ported yet" in the client that actually
// launches (D56). Best Opps was the same case and went first (D178).
//
// ── 🚨 THE DATE RANGE IN THE TOOLBAR DOES NOT APPLY HERE. THE ACCOUNTS DO ──
//
// A review is scoped to its OWN week or month — that is what a review is — so it
// keeps working while the toolbar says "Today". The account filter is honoured,
// because totalling a funded account and a sim together answers nothing. Both
// halves are decided in `selectors/review`, which is where scope belongs, and
// the reasoning is written out there.
//
// ── 🚨 WHAT MUST NOT COME BACK: THE REVIEW ID (D179) ──
//
// Beta identified a review as `week-2026-08-09` — the same string for every user
// — over a GLOBAL primary key, so the first person to save a week locked everyone
// else out of it permanently. A review is found here by its PERIOD, and a new one
// gets an id with real randomness in it. `domain/review` carries the full note.
//
// ── ⚠ THE NUMBERS AND THE FORM FAIL INDEPENDENTLY ──
//
// If the trades read fails, the tiles say so and the reflection form still works.
// The form is the part of this tab only the user can produce, and taking it away
// because an aggregate could not be computed would be the worse trade.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTabMemory } from '../state/tabMemory';
import { useAutosave } from '../state/useAutosave';
// ⚠ ONE DOOR. `ui/` may not import `domain/` (D47), and the io modules are
// reached through the same seam so scope is decided in one place.
import {
  buildBrief, fetchReviewJournals, fetchReviews, isReviewDate, promptSections,
  reviewPeriod, reviewPeriodLabel, reviewSavedLabel, rowsCoverPeriod, selectReview,
  selectReviewNumbers, shiftReviewDate, storeReview,
  type Kpis, type Review, type ReviewCadence, type ReviewJournalEntry,
  type ReviewNumbers, type ReviewPeriod,
} from '../../selectors/review';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { useFilters } from '../state/filters';
import { usePatterns } from '../state/patterns';
import { useRange } from '../state/range';
import { useSupabase } from '../state/supabase';
import { useAuthUser } from '../state/useAuthUser';
import { useProAction } from '../state/useProAction';
import { ProActionLimit } from '../shell/ProActionLimit';
import { PRO_ACTION_LABEL, PRO_ACTION_PITCH } from '../../selectors/proAction';
import { useTradeRows } from '../state/useTradeRows';
import { Icon } from '../shell/Icon';
import { ReviewCoachModal } from './ReviewCoachModal';
import { num, pct, pnlClass, unitValue, unitCompact } from '../format';
import { reportFailure, reportFailureAs } from '../../io/diagnostics/errorLog';

/** The word the nav buttons use. Beta's `Prev ${cadence}` / `This ${cadence}`. */
const WORD: Record<ReviewCadence, string> = { week: 'week', month: 'month' };

/**
 * A client-side id for a review that has never been saved.
 *
 * 🚨 THE RANDOM SUFFIX IS THE WHOLE FIX (D179). The readable prefix is kept only
 * so a row is greppable; it is NOT the identity, and `io/sync/persistedIdShape`
 * fails the build if an id ever goes back to being derived from content alone.
 */
function newReviewId(cadence: ReviewCadence, periodStart: string): string {
  return `${cadence}-${periodStart}-${Math.random().toString(36).slice(2, 10)}`
    + Date.now().toString(36).slice(-4);
}

/**
 * @param go — where a refused Pro action sends the reader. Same destination as
 *   the old client's `showUpgrade`, which navigates to My Account (#76).
 */
export function ReviewTab({ go }: { go?: (tab: string) => void } = {}) {
  // 🚨 READ HERE, PASSED DOWN (#76). `Reflection` owns the Coach Me button, but
  // it is rendered directly by its tests with no providers around it — reading
  // entitlement from a hook inside it made eight of them hang. Same shape as
  // `hasProAccess` taking `paidLaunch`: the component that decides is handed the
  // answer, so the LAUNCHED behaviour is testable without faking a provider.
  const coach = useProAction('ai-coach');
  const { sb } = useSupabase();
  const { user } = useAuthUser();
  const { mode, today } = useRange();
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  const { patterns } = usePatterns();
  const { accounts: selection } = useFilters();

  const [cadence, setCadence] = useTabMemory<ReviewCadence>('review:cadence', 'week');
  /** ⚠ NULL means "today". Held as null rather than seeded so the anchor cannot
      go stale over a session that crosses midnight. */
  const [anchor, setAnchor] = useTabMemory<string | null>('review:anchor', null);
  const date = anchor ?? today ?? '';

  const [reviews, setReviews] = useState<readonly Review[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let live = true;
    fetchReviews(sb)
      .then((rows) => { if (live) { setReviews(rows); setLoadError(null); } })
      // ⚠ A failed read must NOT fall through to a blank form. Every word on this
      // screen was typed by the trader and exists nowhere else — inviting them to
      // write over content that is merely unreachable is the expensive failure.
      // ⚠ `reportFailureAs` and not `reportFailure`, only because the shown text
      // has always been `String(e)` — which for an Error reads `Error: …` where
      // the bare message does not. Funnelling must not restyle a sentence.
      .catch((e: unknown) => {
        if (live) { setReviews(null); setLoadError(reportFailureAs('load reviews', e, String(e))); }
      });
    return () => { live = false; };
  }, [sb]);

  useEffect(() => reload(), [reload]);

  const period: ReviewPeriod | null = useMemo(
    () => (date ? reviewPeriod(cadence, date) : null),
    [cadence, date],
  );

  // ⚠ Minted once per period, not per render. `Math.random()` in the render body
  // would hand every keystroke a different id — harmless until the moment a save
  // raced a re-render.
  const draftId = useMemo(
    () => (period ? newReviewId(cadence, period.start) : ''),
    [cadence, period],
  );

  const review = useMemo(
    () => (period ? selectReview(reviews, cadence, date, draftId) : null),
    [reviews, cadence, date, draftId, period],
  );

  const bounds = useMemo(
    () => (period ? { startDate: period.start, endDate: period.end } : null),
    [period],
  );
  const ranged = useTradeRows(bounds, mode);

  const patternName = useCallback(
    (id: string): string | null => (patterns ?? []).find((p) => p.id === id)?.name ?? null,
    [patterns],
  );

  // ⚠ Read at the TOP, never from inside the JSX below — the early returns for
  // the loading and failed states mean a hook called down there runs on some
  // renders and not others, which React rejects.
  const patternNames = useMemo(
    () => new Map((patterns ?? []).map((p) => [p.id, p.name])),
    [patterns],
  );

  const numbers: ReviewNumbers | null = useMemo(() => {
    // 🚨 THE ROWS MUST BE THIS PERIOD'S, not merely present. The read keeps the
    // previous window's rows while the next one is in flight, so without this
    // the four period buttons paint one frame of the old figures under the new
    // heading — measured on the running app. See `rowsCoverPeriod`.
    if (!rowsCoverPeriod(ranged.loadedBounds, period)) return null;
    if (!ranged.rows || !accounts || !contracts || !mode) return null;
    return selectReviewNumbers({
      trades: ranged.rows,
      accounts,
      contracts,
      selection,
      mode,
      patternName,
    });
    // ⚠ NO `unit` HERE, and none in the dependencies. This tab is dollars, in
    // every figure, because beta's is (#67). The seam no longer accepts one.
  }, [ranged.rows, ranged.loadedBounds, period, accounts, contracts, mode, selection, patternName]);

  if (loadError) {
    return (
      <div className="card">
        <h3>Your reviews could not be loaded</h3>
        <p className="muted">{loadError}</p>
        <button className="btn" onClick={reload}>Try again</button>
      </div>
    );
  }
  if (!reviews || !review || !period) {
    return <div className="card"><p className="muted">Loading…</p></div>;
  }

  const periodLabel = reviewPeriodLabel(cadence, period);
  const savedLabel = reviewSavedLabel(
    review,
    review.updatedAt
      ? new Date(review.updatedAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
      : null,
  );

  return (
    <div className="review-tab">
      <div className="row review-head">
        <div className="grow">
          <h2 className="review-title"><Icon name="review" /> Review</h2>
          <div className="muted review-sub">{periodLabel} · {savedLabel}</div>
        </div>
        <div className="review-chips">
          {(['week', 'month'] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={`period-chip${cadence === c ? ' active' : ''}`}
              aria-pressed={cadence === c}
              onClick={() => setCadence(c)}
            >
              {c === 'week' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
        </div>
      </div>

      <div className="row gap-8 review-nav">
        <button
          type="button" className="btn"
          onClick={() => setAnchor(shiftReviewDate(cadence, date, -1))}
        >
          ‹ Prev {WORD[cadence]}
        </button>
        {/* 🚨 AN EMPTY DATE IS IGNORED, WHICH BETA DOES NOT DO. Its input writes
            straight through, so clearing the field sends '' into the period math
            and the heading reads `NaN-NaN-NaN` until the tab is left. */}
        <input
          type="date" value={date} aria-label="Review date"
          onChange={(e) => { if (isReviewDate(e.target.value)) setAnchor(e.target.value); }}
        />
        <button
          type="button" className="btn"
          onClick={() => setAnchor(shiftReviewDate(cadence, date, 1))}
        >
          Next {WORD[cadence]} ›
        </button>
        <button type="button" className="btn" onClick={() => setAnchor(null)}>
          This {WORD[cadence]}
        </button>
      </div>

      {ranged.phase === 'failed' ? (
        // ⚠ NOT ZEROS. A P&L of "$0.00" over a read that never arrived is a
        // confident claim about a period nobody measured — and the reflection
        // below still works, so the tab is not dead.
        <div className="card review-numbers-error" role="alert">
          <b>This period&apos;s numbers could not be loaded.</b>
          <p className="muted">{ranged.error}</p>
          <p className="muted">Your reflection below is unaffected — it still saves.</p>
        </div>
      ) : !numbers ? (
        <div className="card"><p className="muted">Loading this period&apos;s trades…</p></div>
      ) : (
        <>
          <KpiTiles k={numbers.kpis} />
          <SummaryCards numbers={numbers} />
        </>
      )}

      {/* 🚨 REMOUNTED BY `key`, NOT SEEDED BY AN EFFECT. The form owns the
          trader's typing; stepping to another week has to replace it, and an
          effect that copies the new period's answers into existing state after
          render is the D86 race — one render of the old text under the new
          heading, and a save in that window writes the wrong week's words. */}
      <Reflection
        key={`${cadence}|${period.start}|${review.id}`}
        go={go}
        coach={coach}
        review={review}
        cadence={cadence}
        periodLabel={periodLabel}
        kpis={numbers?.kpis ?? null}
        numbers={numbers}
        patternNames={patternNames}
        canSave={!!user?.id}
        onSave={async (next) => {
          if (!user?.id) return 'You are signed out — sign in again before saving.';
          const res = await storeReview(sb, user.id, next);
          // ⚠ NO REPORT HERE, and it briefly had one. `storeReview` returns
          // `{ ok: false }` for THREE different reasons and this site cannot
          // tell them apart: a failed read, a failed write, and a validation
          // refusal ("this review has no period"). Logging here filed the
          // refusal as a failure — the user's own incomplete form, recorded as
          // if the software broke — and would have filed the other two TWICE
          // once `reviews.ts` was funnelled.
          //
          // ▶ The funnel lives at the two real failures inside `reviews.ts`,
          // where they are distinguishable. See #52.
          if (!res.ok) return res.error;
          // ⚠ The stored row replaces the draft, so the header's "Last edited"
          // comes from the DATABASE's timestamp rather than from this browser's
          // clock — and a second save updates the row it just created.
          setReviews((cur) => {
            const rest = (cur ?? []).filter((r) => r.id !== res.review.id
              && !(r.cadence === res.review.cadence && r.periodStart === res.review.periodStart));
            return [res.review, ...rest];
          });
          return null;
        }}
        loadJournals={() => fetchReviewJournals(sb, period.start, period.end)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The eight tiles
// ---------------------------------------------------------------------------

/**
 * ⚠ ALWAYS IN DOLLARS, and the display-unit toggle does not reach them — beta
 * calls `computeKpis` with no unit and the default is `usd`. Decided in
 * `selectors/review`; stated here so the screen and the seam agree in writing.
 *
 * 🚨 THEY DID NOT AGREE. This paragraph was true when it was written and the
 * seam was changed underneath it (#67), which is why the seam no longer takes a
 * unit at all rather than merely being asked nicely.
 *
 * ⚠ EXPORTED FOR THE TESTS, like `Reflection` below. The tiles carry three
 * things a screenshot caught in beta and a render can pin — the negated
 * drawdown, the `∞` profit factor, and the win-rate colour rule.
 */
export function KpiTiles({ k }: { k: Kpis }) {
  return (
    <div className="review-kpis">
      <div className={`kpi${k.totalPnl > 0 ? ' glow-green' : k.totalPnl < 0 ? ' glow-red' : ''}`}>
        <div className="kpi-label">P&amp;L</div>
        <div className={`kpi-value ${pnlClass(k.totalPnl)}`}>{unitValue(k.totalPnl, k.unit)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Trades</div>
        <div className="kpi-value">{k.count}</div>
        <div className="kpi-sub">{k.wins}W / {k.losses}L</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Win Rate</div>
        <div className={`kpi-value ${winRateClass(k.winRate, k.totalPnl)}`}>
          {pct(k.winRate, 0)}
        </div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Expectancy</div>
        <div className={`kpi-value ${pnlClass(k.expectancy)}`}>{unitValue(k.expectancy, k.unit)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">PF</div>
        {/* ⚠ `∞` IS A REAL ANSWER — winners and no losers — not a divide-by-zero
            to hide. `computeKpis` returns Infinity deliberately and says so. */}
        <div className={`kpi-value ${k.profitFactor >= 1.5 ? 'green' : k.profitFactor < 1 ? 'red' : ''}`}>
          {k.profitFactor === Infinity ? '∞' : num(k.profitFactor, 2)}
        </div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Best Trade</div>
        {/* 🚨 `bestValue`, not the trade’s own `netPnl`, which is always dollars —
            the ranking runs on the unit value, so reading netPnl names the right
            trade and states the wrong result. */}
        <div className="kpi-value green">{unitValue(k.bestValue, k.unit)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Worst Trade</div>
        <div className="kpi-value red">{unitValue(k.worstValue, k.unit)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Max Drawdown</div>
        {/* ⚠ NEGATED. `maxDrawdown` is a positive magnitude; the tile shows the
            loss it represents, which is beta's `fmtMoney(-k.maxDrawdown)`. */}
        <div className="kpi-value red">{unitValue(-k.maxDrawdown, k.unit)}</div>
      </div>
    </div>
  );
}

/**
 * The old client's win-rate colouring, ported literally — and the same function
 * the Patterns tab uses, kept identical rather than shared, because both are
 * three lines of presentation and neither computes anything.
 *
 * ⚠ Red requires a LOSING period as well as a low win rate. A 30%-win period
 * that is up money is a runner strategy working as designed.
 */
function winRateClass(winRate: number, totalPnl: number): string {
  if (winRate >= 55) return 'green';
  if (winRate < 45 && totalPnl < 0) return 'red';
  return '';
}

// ---------------------------------------------------------------------------
// The four cards
// ---------------------------------------------------------------------------

/** ⚠ EXPORTED FOR THE TESTS — the adherence dash is the divergence to pin. */
export function SummaryCards({ numbers }: { numbers: ReviewNumbers }) {
  const s = numbers.summary;
  // ⚠ The unit the KPIs were COMPUTED in, echoed by `computeKpis` so a card
  // cannot mislabel its own number. These tallies are built from the same
  // population in the same unit, so the two can never disagree.
  const unit = numbers.kpis.unit;
  const a = s.adherence;
  return (
    <div className="review-cards">
      <div className="card">
        <h3><Icon name="star" /> Top patterns</h3>
        {s.topPatterns.length ? s.topPatterns.map((p) => (
          <div className="row review-line" key={p.id}>
            <span className="grow">{p.name} <span className="muted">{p.count}t</span></span>
            <span className={`mono ${pnlClass(p.pnl)}`}>{unitCompact(p.pnl, unit)}</span>
          </div>
        )) : <div className="muted review-empty">No tagged patterns this period.</div>}
      </div>

      <div className="card">
        <h3 className="red">Bleeding patterns</h3>
        {s.bottomPatterns.length ? s.bottomPatterns.map((p) => (
          <div className="row review-line" key={p.id}>
            <span className="grow">{p.name} <span className="muted">{p.count}t</span></span>
            <span className="mono red">{unitCompact(p.pnl, unit)}</span>
          </div>
        )) : <div className="muted review-empty">No bleeding patterns this period.</div>}
      </div>

      <div className="card">
        <h3 className="gold"><Icon name="flag" /> Top mistakes</h3>
        {s.topMistakes.length ? s.topMistakes.map((m) => (
          <div className="row review-line" key={m.tag}>
            <span className="grow"><span className="tag red">{m.tag}</span></span>
            <span className="mono">{m.count}</span>
          </div>
        )) : <div className="muted review-empty">No mistake tags this period.</div>}
      </div>

      <div className="card">
        <h3>Process adherence</h3>
        <div className="review-adherence">
          <AdherenceRow label="Plan followed" rate={a.rates['plan']} good={80} bad={60} />
          <AdherenceRow label="A+ setup" rate={a.rates['aPlus']} good={70} bad={50} />
          <AdherenceRow label="Stop honored" rate={a.rates['stop']} good={90} bad={75} />
          {/* ⚠ Uncoloured, as beta leaves it. */}
          <AdherenceRow label="Target honored" rate={a.rates['target']} good={null} bad={null} />
          <div className="row review-adherence-foot">
            <span className="grow">Revenge / FOMO / Overtraded</span>
            <span className={(a.revenge + a.fomo + a.overtraded) === 0 ? 'green' : 'red'}>
              {a.revenge} / {a.fomo} / {a.overtraded}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 🚨 A NULL RATE RENDERS AS `—`, NOT `0%` — A DELIBERATE DIVERGENCE (PD-30).
 *
 * The old client writes `adherenceRate(...) ?? 0`, so a period in which nothing
 * has been annotated reports "you followed your plan on 0% of your trades" about
 * a trader who has simply not filled the review in yet. It is the same defect
 * D130 removed from the Analytics overview (PD-21) and D118 before that.
 *
 * ⚠ It is VISIBLE on Josh's own screenshot, which shows `0%` on all four rows of
 * an unannotated week. This screen shows four dashes there instead. Registered in
 * PARITY-DELTAS so a side-by-side does not read it as a regression.
 */
function AdherenceRow(
  { label, rate, good, bad }:
  { label: string; rate: number | null | undefined; good: number | null; bad: number | null },
) {
  const cls = rate == null || good == null || bad == null
    ? '' : rate >= good ? 'green' : rate < bad ? 'red' : '';
  return (
    <div className="row">
      <span className="grow">{label}</span>
      <span className={cls}>{rate == null ? '—' : `${rate}%`}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The reflection, and the three buttons under it
// ---------------------------------------------------------------------------

interface ReflectionProps {
  /** Where a refused Pro action sends the reader (#76). Absent in tests. */
  go?: ((tab: string) => void) | undefined;
  /**
   * 🚨 May this reader run the AI Coach?
   *
   * The Review tab is FREE in both clients and carries a Coach Me button, so the
   * tab gate never touches it — beta guards the run itself (#76). Handed in
   * rather than read here so this component still renders on its own.
   *
   * ⚠ Absent means ALLOWED, which is the same direction the entitlement layer
   * fails in and for the same reason: refusing a paying customer costs more than
   * one free run does.
   */
  coach?: { blocked: string | null } | undefined;
  review: Review;
  cadence: ReviewCadence;
  periodLabel: string;
  kpis: Kpis | null;
  numbers: ReviewNumbers | null;
  patternNames: ReadonlyMap<string, string>;
  canSave: boolean;
  onSave: (r: Review) => Promise<string | null>;
  loadJournals: () => Promise<ReviewJournalEntry[]>;
}

/**
 * ⚠ EXPORTED FOR THE TESTS. Everything risky on this tab is in here — the draft
 * that must survive a failed save, the brief that must carry unsaved typing, and
 * the refusal to claim a save that did not happen.
 */
export function Reflection(
  {
    review, cadence, periodLabel, kpis, numbers, patternNames, canSave, onSave,
    loadJournals, go, coach,
  }: ReflectionProps,
) {
  const [coachRefused, setCoachRefused] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>(() => ({ ...review.answers }));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coachBrief, setCoachBrief] = useState<string | null>(null);

  const set = (key: string, value: string) =>
    setAnswers((s) => ({ ...s, [key]: value }));

  /** The review AS TYPED — never the last saved one. See the header. */
  const current = useMemo((): Review => ({ ...review, answers }), [review, answers]);

  // ── 🔴 AUTOSAVE (TCR-80) ──
  //
  // Josh lost a premarket plan to a tab switch and the rule that came out of it
  // is flat: *"This should auto save all the time, on any tab where the user
  // manually inputs text. We shouldn't ever lose data."* This tab was one of the
  // surfaces that did not — ten free-text prompts, committed only by the button.
  //
  // ⚠ WHY THIS TAB AND NOT THE FOUR EDITORS THE CARD ALSO NAMED. Every surface
  // in this client that commits on blur — `Backtests`, `Practice`,
  // `ConfluenceEditor`, `CustomTagEditor`, `MistakeTagEditor`, `Settings` — is an
  // IN-PAGE card like this one. The trade, pattern, exit-model and best-opp
  // editors are MODALS with a Cancel that discards, and autosaving behind a
  // Cancel makes the button a lie. The convention was already settled in six
  // places; this follows it rather than breaking it in four more.
  //
  // ⚠ COMPARED FIELD BY FIELD, NOT BY `JSON.stringify`. The answers blob is
  // JSONB and Postgres does not promise key order back, so a stringify compare
  // would call a freshly-saved review dirty and write it again on the next blur.
  const dirty = useMemo(() => {
    for (const k of new Set([...Object.keys(answers), ...Object.keys(review.answers)])) {
      if ((answers[k] ?? '') !== (review.answers[k] ?? '')) return true;
    }
    return false;
  }, [answers, review.answers]);

  // ⚠ NO `setBusy` HERE, deliberately. `busy` disables Coach Me, Copy and Save,
  // so flipping it on a blur would grey out the footer every time the trader
  // moved between prompts. A failed write reports and leaves the form dirty, so
  // the next blur retries it — the same contract the hook documents.
  const autosave = useAutosave({
    dirty,
    enabled: canSave,
    commit: async () => {
      const e = await onSave(current);
      if (e) setError(e);
      else { setError(null); setStatus('Review saved'); }
    },
  });

  /**
   * ⚠ THE JOURNAL IS READ HERE, ON A PRESS. Nothing on screen shows a journal
   * entry; they exist only inside the brief, so paying for that read on every
   * page view would be a request most visits never use.
   */
  const brief = useCallback(async (): Promise<string> => {
    const journals = await loadJournals();
    return buildBrief({
      review: current,
      kpis: kpis ?? { count: 0, totalPnl: 0, winRate: 0, profitFactor: 0, expectancy: 0 },
      summary: numbers?.summary ?? {
        topPatterns: [], bottomPatterns: [], topMistakes: [],
        adherence: { rates: {}, revenge: 0, fomo: 0, overtraded: 0 },
      },
      journals,
      patternNames,
    });
  }, [current, kpis, numbers, patternNames, loadJournals]);

  return (
    <>
      <div className="card review-reflection">
        <h3><Icon name="review" /> Reflection</h3>
        <div className="muted review-reflection-note">
          Free-text. Each prompt saves as you move off it — Save Review below is there
          if you want to be sure. Skip any that don&apos;t apply.
        </div>
        {promptSections().map(({ section, prompts }) => (
          <div key={section}>
            <h4 className="review-section">{section.toUpperCase()}</h4>
            {prompts.map((p) => (
              <div className="field review-field" key={p.key}>
                {/* ⚠ `field-label` is this client's class for the small-caps
                    grey label beta writes as a bare `.field label` rule. Without
                    it the prompt renders at default heading weight and case —
                    white sentence text where beta has 10px uppercase. */}
                <label className="field-label" htmlFor={`rprompt-${p.key}`}>{p.label}</label>
                <textarea
                  id={`rprompt-${p.key}`} rows={3} placeholder={p.placeholder}
                  value={answers[p.key] ?? ''}
                  onChange={(e) => set(p.key, e.target.value)}
                  onBlur={autosave.onBlur}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="row review-foot">
        {/* 🚨 NEITHER A SUCCESS NOR A FAILURE IS SILENT, and they are never both.
            Beta logged "Review saved" in the same SECOND as "Save failed
            (reviews-upsert)" because it discarded the result it was handed.
            ⚠ ONE mechanism, deliberately: the success message exists only if
            `setStatus` was reached, which happens only on a success. An extra
            `!error &&` guard here would ALSO hide it — and two mechanisms mean
            either can rot without anything going red. Both are cleared at the
            press, so they cannot be stale together. */}
        {error ? <span className="review-status red" role="alert">{error}</span> : null}
        {status ? <span className="review-status muted" role="status">{status}</span> : null}
        <span className="grow" />
        <button
          type="button" className="btn" disabled={busy}
          onClick={() => {
            // ⚠ BEFORE THE BRIEF IS BUILT, not after. Building it reads the
            // whole period and is the expensive half of the click; refusing
            // after that work is done would be a stall the reader cannot explain.
            if (coach?.blocked) { setCoachRefused(true); return; }
            setError(null); setStatus(null);
            void brief()
              .then((text) => setCoachBrief(text))
              // ⚠ `String(e)` is kept as the SHOWN half — see the read above.
              .catch((e: unknown) => setError(
                `The brief could not be built: ${reportFailureAs('build review brief', e, String(e))}`,
              ));
          }}
        >
          <Icon name="ai" /> Coach Me — This Review
        </button>
        <button
          type="button" className="btn" disabled={busy}
          onClick={() => {
            setError(null); setStatus(null);
            void (async () => {
              try {
                const text = await brief();
                await navigator.clipboard.writeText(text);
                setStatus('Review copied to clipboard as Markdown');
              } catch (e) {
                // ⚠ Says what to do instead rather than failing silently — a
                // clipboard write is blocked outright in some browsers.
                setError(`Clipboard failed: ${reportFailure('copy review to clipboard', e)}`);
              }
            })();
          }}
        >
          Copy as Markdown
        </button>
        <button
          type="button" className="btn primary" disabled={busy || !canSave}
          onClick={() => {
            setBusy(true); setError(null); setStatus(null);
            void onSave(current)
              .then((e) => {
                if (e) setError(e);
                else setStatus('Review saved');
              })
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Saving…' : 'Save Review'}
        </button>
      </div>

      {coachRefused ? (
        <ProActionLimit
          title={PRO_ACTION_LABEL['ai-coach']}
          message={coach?.blocked ?? ''}
          pitch={PRO_ACTION_PITCH['ai-coach']}
          onClose={() => setCoachRefused(false)}
          onSeePlans={go ? () => go('account') : undefined}
        />
      ) : null}

      {coachBrief != null ? (
        <ReviewCoachModal
          brief={coachBrief}
          // ⚠ Keyed by the REVIEW ROW, so the two runs are per period rather than
          // per browser session — re-opening a week must not buy more runs.
          cacheKey={`review:${review.id}`}
          cadenceLabel={cadence === 'week' ? 'Weekly' : 'Monthly'}
          periodLabel={periodLabel}
          onClose={() => setCoachBrief(null)}
        />
      ) : null}
    </>
  );
}
